import { useQuery } from "@tanstack/react-query";

const QUERY_TIMEOUT_MS = 8000;
const REST_BASE_URL = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1`;
const REST_HEADERS: HeadersInit = {
  apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
  Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
};

export interface Category {
  id: string;
  name: string;
  image: string | null;
  slug: string;
  display_size?: string;
  show_in_menu?: boolean;
  show_in_shop_by_category?: boolean;
  show_on_home?: boolean;
}

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number): Promise<T> =>
  Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      globalThis.setTimeout(() => reject(new Error("categories_timeout")), timeoutMs);
    }),
  ]);

const fetchCategoriesREST = async (
  columns: string,
  filterKey?: string,
): Promise<Category[]> => {
  try {
    const url = new URL(`${REST_BASE_URL}/categories`);
    url.searchParams.set("select", columns);
    if (filterKey) url.searchParams.set(filterKey, "eq.true");
    url.searchParams.set("order", "sort_order.asc");

    const response = await withTimeout(
      fetch(url.toString(), { headers: REST_HEADERS }),
      QUERY_TIMEOUT_MS,
    );

    if (!response.ok) throw new Error(`categories_http_${response.status}`);
    return (await response.json()) as Category[];
  } catch (error) {
    console.error("categories load failed", error);
    return [];
  }
};

const fetchAllCategories = (): Promise<Category[]> =>
  fetchCategoriesREST("id,name,image,slug,display_size,show_in_menu,show_in_shop_by_category,show_on_home");

export const useAllCategoriesBase = (enabled = true) => {
  return useQuery({
    queryKey: ["categories", "all"],
    queryFn: fetchAllCategories,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled,
  });
};

/** Menu categories (Header, dropdowns) */
export const useMenuCategories = (enabled = true) => {
  return useQuery({
    queryKey: ["categories", "menu"],
    queryFn: () => fetchCategoriesREST("id,name,image,slug", "show_in_menu"),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled,
  });
};

/** Shop-by-category section */
export const useShopCategories = (enabled = true, compact = false) => {
  return useQuery({
    queryKey: ["categories", "shop", compact ? "compact" : "full"],
    queryFn: () =>
      fetchCategoriesREST(
        compact ? "id,name,slug" : "id,name,image,slug,display_size",
        "show_in_shop_by_category",
      ),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled,
  });
};

/** Home categories */
export const useHomeCategories = (enabled = true) => {
  return useQuery({
    queryKey: ["categories", "home"],
    queryFn: () => fetchCategoriesREST("id,name,slug", "show_on_home"),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled,
  });
};
